import { useApolloClient } from "@apollo/client";
import {
  ButtonInputWithPopover,
  ListHeaderActiveFiltersActionButton,
  Modal,
  ModalOpenButton,
  PopoverOpenButtonRef,
} from "@msys/ui";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import {
  useAddListViewCustomQuickFilterMutation,
  useListViewCustomQuickFiltersQuery,
  useRemoveListViewCustomQuickFilterMutation,
  useRenameListViewCustomQuickFilterMutation,
} from "./useListViewCustomQuickFilters.generated.js";

export function useListViewCustomQuickFilters<Filters>(
  listViewName: string,
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>,
  fixedQuickFilters: {
    filterName: string;
    filterValue: Filters;
  }[] = [],
  getFilterChips?: (args: { filters: Filters }) => Array<JSX.Element>
) {
  const { t } = useTranslate(["Global"]);

  const client = useApolloClient();
  const query = useListViewCustomQuickFiltersQuery({
    client,
    variables: {
      listViewName,
    },
  });
  const [addFilter] = useAddListViewCustomQuickFilterMutation({
    client,
    refetchQueries: [namedOperations.Query.ListViewCustomQuickFilters],
    awaitRefetchQueries: true,
  });

  const quickFilters = [
    ...fixedQuickFilters.map(quickFilter => ({
      id: `fixed-${quickFilter.filterName}`,
      filterName: quickFilter.filterName,
      filterValue: quickFilter.filterValue,
    })),
    ...(query.data?.viewerQuickFilters ?? []),
  ];

  const popoverRef = React.useRef<PopoverOpenButtonRef>();

  const customQuickFilterSelect =
    quickFilters && quickFilters.length > 0 ? (
      <ButtonInputWithPopover
        label={t("My filters", { ns: "Global" })}
        popoverWidth="md"
        popoverRef={popoverRef}
        content={
          <List dense disablePadding>
            {quickFilters.map(filter => (
              <ListItemButton key={filter.id}>
                <ListItemText
                  primary={filter.filterName}
                  onClick={() => {
                    const selectedFilter = quickFilters?.find(
                      f => f.id === filter.id
                    )?.filterValue;
                    if (selectedFilter) {
                      setFilters(selectedFilter);
                      popoverRef.current?.handleClose();
                    }
                  }}
                />
              </ListItemButton>
            ))}
            <Divider />
            {quickFilters.some(filter => !filter.id.startsWith("fixed-")) && (
              <ModalOpenButton
                Modal={EditCustomFiltersModal<Filters>}
                modalProps={{
                  customFilters: quickFilters,
                  getFilterChips,
                }}
                onCloseCallback={popoverRef.current?.handleClose}
              >
                <ListItemButton sx={{ color: "secondary.main" }}>
                  <ListItemIcon
                    sx={{ minWidth: 0, marginRight: 1 }}
                    color="inherit"
                  >
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t("Edit", { ns: "Global" })}
                    color="inherit"
                  />
                </ListItemButton>
              </ModalOpenButton>
            )}
          </List>
        }
      />
    ) : null;

  const addCustomFilterButton = filters ? (
    <ModalOpenButton
      Modal={GetCustomFilterNameModal<Filters>}
      modalProps={{
        title: t("Save filter", { ns: "Global" }),
        handleComplete: async (filterName: string, handleClose) => {
          await addFilter({
            variables: {
              input: {
                listViewName,
                filterName,
                filterValue: filters,
              },
            },
          });

          handleClose();
        },
        filters,
        getFilterChips,
      }}
    >
      <ListHeaderActiveFiltersActionButton
        label={t("Save filter", { ns: "Global" })}
      />
    </ModalOpenButton>
  ) : null;

  return {
    customQuickFilterSelect,
    addCustomFilterButton,
  };
}

const GetCustomFilterNameModal = <Filters,>({
  handleClose,
  handleComplete,
  title,
  inputLabel,
  filters,
  getFilterChips,
}: {
  handleClose: () => void;
  handleComplete: (filterName: string, handleClose: () => void) => void;
  title: string;
  inputLabel?: string;
  filters: Filters;
  getFilterChips?: (args: { filters: Filters }) => Array<JSX.Element>;
}) => {
  const { t } = useTranslate(["Global"]);

  const [filterName, setFilterName] = React.useState("");

  return (
    <Modal
      handleClose={handleClose}
      title={title}
      actionButtons={[
        {
          label: t("Confirm", { ns: "Global" }),
          handleClick: () => {
            handleComplete(filterName, handleClose);
          },
          buttonProps: {
            disabled: filterName.trim() === "",
          },
        },
      ]}
    >
      <Stack spacing={2}>
        <TextField
          label={inputLabel ?? t("Filter name", { ns: "Global" })}
          value={filterName}
          onChange={event => {
            setFilterName(event.target.value);
          }}
        />
        {getFilterChips && (
          <Stack direction="row" spacing={0} gap={1} flexWrap="wrap">
            {getFilterChips({ filters })}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

const EditCustomFiltersModal = <Filters,>({
  handleClose,
  customFilters,
  getFilterChips,
}: {
  handleClose: () => void;
  customFilters: Array<{
    id: string;
    filterName: string;
    filterValue: Filters;
  }>;
  getFilterChips?: (args: { filters: Filters }) => Array<JSX.Element>;
}) => {
  const { t } = useTranslate(["Global"]);

  const client = useApolloClient();
  const [renameFilter] = useRenameListViewCustomQuickFilterMutation({
    client,
    refetchQueries: [namedOperations.Query.ListViewCustomQuickFilters],
    awaitRefetchQueries: true,
  });
  const [removeFilter] = useRemoveListViewCustomQuickFilterMutation({
    client,
    refetchQueries: [namedOperations.Query.ListViewCustomQuickFilters],
    awaitRefetchQueries: true,
  });

  return (
    <Modal
      handleClose={handleClose}
      title={t("Edit filters", { ns: "Global" })}
      alwaysVisible
    >
      <List dense>
        {customFilters
          .filter(filter => !filter.id.startsWith("fixed-"))
          .map((filter, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider />}
              <ListItem
                disableGutters
                disablePadding
                secondaryAction={
                  filter.id.startsWith("fixed-") ? null : (
                    <Stack direction="row" spacing={1}>
                      <ModalOpenButton
                        Modal={GetCustomFilterNameModal<Filters>}
                        modalProps={{
                          title: t("Rename filter {filterName}", {
                            ns: "Global",
                            filterName: filter.filterName,
                          }),
                          inputLabel: t("New filter name", { ns: "Global" }),
                          handleComplete: async (filterName, handleClose) => {
                            await renameFilter({
                              variables: {
                                input: {
                                  id: filter.id,
                                  newFilterName: filterName,
                                },
                              },
                            });
                            handleClose();
                          },
                          filters: filter.filterValue,
                          getFilterChips,
                        }}
                      >
                        <IconButton color="primary" size="small">
                          <EditIcon />
                        </IconButton>
                      </ModalOpenButton>

                      <ModalOpenButton
                        Modal={ConfirmModal}
                        modalProps={{
                          title: t("Confirm delete", { ns: "Global" }),
                          text: t(
                            "Are you sure you want to delete this filter?",
                            {
                              ns: "Global",
                            }
                          ),
                          handleConfirm: async () => {
                            await removeFilter({
                              variables: { input: { id: filter.id } },
                            });
                          },
                        }}
                      >
                        <IconButton color="primary" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </ModalOpenButton>
                    </Stack>
                  )
                }
              >
                <ListItemText
                  disableTypography
                  primary={
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography>{filter.filterName}</Typography>
                    </Stack>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
      </List>
    </Modal>
  );
};
